import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select,
    FormControl, InputLabel, CircularProgress, Autocomplete, Snackbar, Alert,
    Popover, Divider, Chip, Switch, FormControlLabel, Badge
} from '@mui/material'
import { Search as SearchIcon, Add, FilterList, Edit, Phone, Refresh } from '@mui/icons-material'
import { FC, useCallback, useEffect, useState } from 'react'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api, numberWithCommas, getDurationSince } from '../../helpers'
import { countries, Country, getCurrency } from '../../helpers/countries'
import { Connect, mapStateToProps } from '../../Redux'
import { Search, SearchIconWrapper, StyledInputBase } from '../Home'
import _ from 'lodash'

interface Device {
    placeName: string
    pricePerGame: number
    tableName: string
    deviceId: string
    isOn: boolean
    lastStateChangeAt?: string
    creditAmount: number
    batteryLevel: number
    angle: number
    owner: string
    phone: string
    country: string
    AccountNo: string
    clientId: string
    // false for an inventory row registered ahead of an owner being assigned (see
    // spotpool_server's devices.ts registerDevice/installDevice) - has no owner/place/table/
    // online-state yet, only the account number and gadget config it was registered with.
    installed: boolean
}

interface Client {
    _id: string
    Username: string
    Phone: string
}

interface FormState {
    clientId: string
    placeName: string
    tableName: string
    angle: string
    pricePerGame: string
    creditAmount: string
    country: Country | null
    AccountNo: string
}

interface Filters {
    country: string
    place: string
    hasCredit: boolean
    status: 'all' | 'on' | 'off'
}

interface StateProps {
    devices: Device[]
    loading: boolean
    searchVal: string | null
    showModal: boolean
    editingDeviceId: string | null
    clientOptions: Client[]
    clientsLoading: boolean
    selectedClient: Client | null
    saving: boolean
    form: FormState
    error: string | null
    filterAnchor: HTMLElement | null
    filters: Filters
    phoneAnchor: HTMLElement | null
    selectedPhone: string | null
    copied: boolean
    accountNoLoading: boolean
}

const defaultFilters: Filters = {
    country: '',
    place: '',
    hasCredit: false,
    status: 'all'
}

const defaultForm: FormState = {
    clientId: '',
    placeName: '',
    tableName: '',
    angle: '',
    pricePerGame: '',
    creditAmount: '',
    country: null,
    AccountNo: ''
}

const headers = ['Device ID', 'Table', 'Place', 'Country', 'Owner', 'Account No.', 'Angle', 'Credit', 'Price/Game', 'Battery', 'Installed', 'Status', 'Actions']

const Devices: FC = () => {
    const [state, setState] = useState<StateProps>({
        devices: [],
        loading: true,
        searchVal: null,
        showModal: false,
        editingDeviceId: null,
        clientOptions: [],
        clientsLoading: false,
        selectedClient: null,
        saving: false,
        form: defaultForm,
        error: null,
        filterAnchor: null,
        filters: defaultFilters,
        phoneAnchor: null,
        selectedPhone: null,
        copied: false,
        accountNoLoading: false
    })
    const { devices, loading, searchVal, showModal, editingDeviceId, clientOptions, clientsLoading, selectedClient, saving, form, error, filterAnchor, filters, phoneAnchor, selectedPhone, copied, accountNoLoading } = state

    const setFilter = (patch: Partial<Filters>) =>
        setState(s => ({ ...s, filters: { ...s.filters, ...patch } }))

    const activeFilterCount = [
        filters.country,
        filters.place,
        filters.hasCredit,
        filters.status !== 'all'
    ].filter(Boolean).length

    const getDevices = ({ searchVal, applyFilters }: { searchVal?: string | null; applyFilters?: Filters } = {}) => {
        setState(s => ({ ...s, loading: true }))
        const f = applyFilters
        Api({
            controller: 'Devices',
            action: searchVal ? 'searchDevices' : 'getDevices',
            searchVal,
            filter: {
                ...(f?.country && { country: f.country }),
                ...(f?.place && { place: f.place }),
                ...(f?.hasCredit && { hasCredit: true }),
                ...(f?.status && f.status !== 'all' && { status: f.status })
            }
        }).then((resp: any) => {
            setState(s => ({ ...s, devices: resp, loading: false, searchVal: searchVal ?? null }))
        }).catch(() => {
            setState(s => ({ ...s, loading: false }))
        })
    }

    const searchUser = (value: string) => {
        if (!value) return setState(s => ({ ...s, clientOptions: [], clientsLoading: false }))
        setState(s => ({ ...s, clientsLoading: true }))
        Api({ controller: 'Users', action: 'searchUser', value })
            .then((resp: any) => setState(s => ({ ...s, clientOptions: resp, clientsLoading: false })))
            .catch(() => setState(s => ({ ...s, clientsLoading: false })))
    }
    const debouncedSearchUser = useCallback(_.debounce(searchUser, 600), [])

    const searchDevices = (val: string) => getDevices({ searchVal: val })
    const startSearch = useCallback(_.debounce(searchDevices, 1000), [])

    // One past the highest account number already in use for a country (see spotpool_server's
    // devices.ts getNextAccountNo) - replaces having the admin type one by hand and risk reusing
    // one already claimed. Only relevant while adding (see the Account No. field below) - editing
    // an existing device keeps its own real number, manually correctable if it's ever wrong.
    const fetchNextAccountNo = (countryName?: string) => {
        if (!countryName) return
        setState(s => ({ ...s, accountNoLoading: true }))
        Api({ controller: 'Devices', action: 'getNextAccountNo', country: countryName }).then((resp: any) => {
            if (resp?.error) return setState(s => ({ ...s, accountNoLoading: false }))
            setState(s => ({ ...s, accountNoLoading: false, form: { ...s.form, AccountNo: String(resp.accountNo) } }))
        }).catch(() => setState(s => ({ ...s, accountNoLoading: false })))
    }

    const openModal = () => setState(s => ({ ...s, showModal: true, editingDeviceId: null, form: defaultForm, clientOptions: [], selectedClient: null }))

    const openEdit = (device: Device) => {
        const countryObj = countries.find(c => c.name === device.country) ?? null
        const preselectedClient: Client = { _id: device.clientId, Username: device.owner, Phone: device.phone }
        setState(s => ({
            ...s,
            showModal: true,
            editingDeviceId: device.deviceId,
            clientOptions: [preselectedClient],
            selectedClient: preselectedClient,
            form: {
                clientId: device.clientId,
                placeName: device.placeName,
                tableName: device.tableName,
                angle: String(device.angle),
                pricePerGame: String(device.pricePerGame),
                creditAmount: String(device.creditAmount),
                country: countryObj,
                AccountNo: device.AccountNo
            }
        }))
    }

    const closeModal = () => setState(s => ({ ...s, showModal: false }))

    const setForm = (patch: Partial<FormState>) =>
        setState(s => ({ ...s, form: { ...s.form, ...patch } }))

    const saveDevice = () => {
        const payload = {
            ...form,
            angle: Number(form.angle),
            pricePerGame: Number(form.pricePerGame),
            creditAmount: Number(form.creditAmount),
            country: form.country?.name,
            countryCode: form.country?.code
        }
        setState(s => ({ ...s, saving: true }))
        Api({
            controller: 'Devices',
            action: editingDeviceId ? 'editDevice' : 'addDevice',
            deviceId: editingDeviceId,
            ...payload
        }).then((resp: any) => {
            console.log(resp)
            if (resp.error)
                setState(s => ({ ...s, saving: false, error: resp.error.message }))
            else {
                setState(s => ({ ...s, saving: false, showModal: false }))
                getDevices()
            }
        }).catch((err) => {
            setState(s => ({ ...s, saving: false, error: err?.message || 'Something went wrong. Please try again.' }))
        })
    }

    useEffect(() => {
        getDevices()
    }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Devices</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', px: '5px' }}>
                <Search sx={{ borderRadius: 3, flex: 1, margin: 0 }}>
                    <SearchIconWrapper>
                        <SearchIcon sx={{ fontSize: 20, color: 'black' }} />
                    </SearchIconWrapper>
                    <StyledInputBase
                        placeholder='Search owner'
                        inputProps={{ 'aria-label': 'search' }}
                        onChange={(e) => startSearch(e.target.value)}
                    />
                </Search>
                <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                    <Badge badgeContent={activeFilterCount} color='error'>
                        <Button variant='outlined' startIcon={<FilterList />} sx={{ borderRadius: 2, whiteSpace: 'nowrap' }} onClick={e => setState(s => ({ ...s, filterAnchor: e.currentTarget }))}>
                            Filter
                        </Button>
                    </Badge>
                    <Button variant='contained' startIcon={<Add />} onClick={openModal} sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}>
                        Add Device
                    </Button>
                </Box>
            </Box>
            <Popover
                open={Boolean(phoneAnchor)}
                anchorEl={phoneAnchor}
                onClose={() => setState(s => ({ ...s, phoneAnchor: null, selectedPhone: null, copied: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 220 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 13, color: '#727A8A' }}>Phone Number</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 'bold', letterSpacing: 0.5 }}>{selectedPhone}</Typography>
                    <Button
                        variant={copied ? 'outlined' : 'contained'}
                        size='small'
                        color={copied ? 'success' : 'primary'}
                        onClick={() => {
                            navigator.clipboard.writeText(selectedPhone ?? '')
                            setState(s => ({ ...s, copied: true }))
                        }}
                    >
                        {copied ? 'Copied!' : 'Copy'}
                    </Button>
                </Box>
            </Popover>

            <Popover
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                onClose={() => setState(s => ({ ...s, filterAnchor: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 280 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>Filters</Typography>
                        <Button size='small' onClick={() => {
                            setState(s => ({ ...s, filters: defaultFilters, filterAnchor: null }))
                            getDevices()
                        }}>Clear all</Button>
                    </Box>
                    <Divider />
                    <TextField
                        fullWidth
                        size='small'
                        label='Country'
                        value={filters.country}
                        onChange={e => setFilter({ country: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        size='small'
                        label='Place'
                        value={filters.place}
                        onChange={e => setFilter({ place: e.target.value })}
                    />
                    <FormControl fullWidth size='small'>
                        <InputLabel>Status</InputLabel>
                        <Select value={filters.status} label='Status' onChange={e => setFilter({ status: e.target.value as Filters['status'] })}>
                            <MenuItem value='all'>All</MenuItem>
                            <MenuItem value='on'>On</MenuItem>
                            <MenuItem value='off'>Off</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={<Switch checked={filters.hasCredit} onChange={e => setFilter({ hasCredit: e.target.checked })} />}
                        label='Has credit'
                    />
                    <Button
                        variant='contained'
                        fullWidth
                        onClick={() => {
                            setState(s => ({ ...s, filterAnchor: null }))
                            getDevices({ applyFilters: filters })
                        }}
                    >
                        Apply Filters
                    </Button>
                </Box>
            </Popover>

            <Show>
                <Show.When isTrue={loading || !!(searchVal && !devices.length)}>
                    <EmptyView
                        loading={loading}
                        oopTxt='No Devices'
                        text='No devices have been added yet.'
                    />
                </Show.When>
                <Show.Else>
                    <TableContainer component={Paper} sx={{ mt: 2, mx: '5px' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {headers.map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {devices.map(({ deviceId, placeName, tableName, isOn, lastStateChangeAt, creditAmount, batteryLevel, pricePerGame, angle, owner, phone, country, AccountNo, clientId, installed }) => (
                                    <TableRow key={deviceId} hover>
                                        <TableCell>{deviceId}</TableCell>
                                        <TableCell>{tableName || '—'}</TableCell>
                                        <TableCell>{placeName || '—'}</TableCell>
                                        <TableCell>{country}</TableCell>
                                        <TableCell>{owner || '—'}</TableCell>
                                        <TableCell>{AccountNo}</TableCell>
                                        <TableCell>{angle}°</TableCell>
                                        <TableCell>{getCurrency(country)} {numberWithCommas(creditAmount)}</TableCell>
                                        <TableCell>{installed ? `${getCurrency(country)} ${numberWithCommas(pricePerGame)}` : '—'}</TableCell>
                                        <TableCell>{installed ? `${batteryLevel}%` : '—'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size='small'
                                                label={installed ? 'Installed' : 'Not Installed'}
                                                sx={{
                                                    bgcolor: installed ? 'rgba(0,107,70,0.1)' : 'rgba(208,133,10,0.15)',
                                                    color: installed ? '#006B46' : '#8a6d00',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {installed ? (
                                                <>
                                                    <Typography sx={{ color: isOn ? 'green' : 'red', fontWeight: 'bold', fontSize: 14 }}>
                                                        {isOn ? 'On' : 'Off'}
                                                    </Typography>
                                                    {!isOn && getDurationSince(lastStateChangeAt) && (
                                                        <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                                                            for {getDurationSince(lastStateChangeAt)}
                                                        </Typography>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {installed ? (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button size='small' variant='outlined' startIcon={<Edit fontSize='small' />} sx={{ borderRadius: 2 }} onClick={() => openEdit({ deviceId, placeName, tableName, isOn, creditAmount, batteryLevel, pricePerGame, angle, owner, phone, country, AccountNo, clientId, installed })}>
                                                        Edit
                                                    </Button>
                                                    <Button size='small' variant='contained' color='success' startIcon={<Phone fontSize='small' />} sx={{ borderRadius: 2 }} onClick={e => setState(s => ({ ...s, phoneAnchor: e.currentTarget, selectedPhone: phone, copied: false }))}>
                                                        Call
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>Install via mobile app</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Show.Else>
            </Show>

            <Dialog open={showModal} onClose={closeModal} fullWidth maxWidth='sm'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>{editingDeviceId ? 'Edit Device' : 'Add Device'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <Autocomplete
                        disabled={Boolean(editingDeviceId)}
                        options={clientOptions}
                        getOptionLabel={(o) => o.Username}
                        loading={clientsLoading}
                        value={selectedClient}
                        onChange={(_, val) => setState(s => ({ ...s, selectedClient: val, form: { ...s.form, clientId: val?._id ?? '' } }))}
                        onInputChange={(_, val) => debouncedSearchUser(val)}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        renderOption={(props, o) => (
                            <li {...props} key={o._id}>
                                <Box>
                                    <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>{o.Username}</Typography>
                                    <Typography sx={{ color: '#727A8A', fontSize: 12 }}>{o.Phone}</Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label='Search Client'
                                placeholder='Type username...'
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {clientsLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />
                    <Autocomplete
                        options={countries}
                        getOptionLabel={(o) => `${o.flag} ${o.name}`}
                        value={form.country}
                        onChange={(_, val) => {
                            setForm({ country: val })
                            // Account numbers are only unique per country, so a number suggested
                            // for the previous one may already be taken in the new one.
                            if (!editingDeviceId) fetchNextAccountNo(val?.name)
                        }}
                        isOptionEqualToValue={(o, v) => o.code === v.code}
                        renderInput={(params) => <TextField {...params} label='Country' />}
                    />
                    <TextField
                        label='Place Name'
                        value={form.placeName}
                        onChange={e => setForm({ placeName: e.target.value })}
                        fullWidth
                    />
                    <TextField
                        label='Table Name'
                        value={form.tableName}
                        onChange={e => setForm({ tableName: e.target.value })}
                        fullWidth
                    />
                    <TextField
                        label='Angle (°)'
                        type='number'
                        value={form.angle}
                        onChange={e => setForm({ angle: e.target.value })}
                        fullWidth
                    />
                    <TextField
                        label={`Price Per Game (${form.country ? getCurrency(form.country.name) : 'currency'})`}
                        type='number'
                        value={form.pricePerGame}
                        onChange={e => setForm({ pricePerGame: e.target.value })}
                        fullWidth
                    />
                    <TextField
                        label={`Credit Amount (${form.country ? getCurrency(form.country.name) : 'currency'})`}
                        type='number'
                        value={form.creditAmount}
                        onChange={e => setForm({ creditAmount: e.target.value })}
                        fullWidth
                    />
                    <TextField
                        label='Account No.'
                        value={accountNoLoading ? 'Assigning...' : form.AccountNo}
                        onChange={e => setForm({ AccountNo: e.target.value })}
                        fullWidth
                        disabled={!editingDeviceId}
                        InputProps={!editingDeviceId ? {
                            endAdornment: (
                                <Button
                                    size='small'
                                    onClick={() => fetchNextAccountNo(form.country?.name)}
                                    disabled={accountNoLoading || !form.country}
                                >
                                    {accountNoLoading ? <CircularProgress size={16} /> : <Refresh fontSize='small' />}
                                </Button>
                            )
                        } : undefined}
                        helperText={!editingDeviceId ? 'Auto-assigned for the selected country' : undefined}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeModal} disabled={saving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={saveDevice}
                        disabled={saving || (!editingDeviceId && !form.clientId) || !form.tableName}
                        startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        {editingDeviceId ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Connect(mapStateToProps)(Devices)
